import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { OrganizationSettingsView } from "@gioia365/contracts";
import { locales } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { settingsQuery, useTab, useUpdateSettings } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { DataTable } from "../../design/components/DataTable";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericDate, formatNumericDateTime } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import {
  MAX_CLOSURES,
  WEEKDAY_KEYS,
  WEEKDAY_NAME,
  addNationalHolidays,
  calendarProblems,
  clockOptions,
  describeCalendarChanges,
  draftFromSettings,
  nextNationalHolidays,
  todayIn,
  updateBodyFrom,
  type CalendarDraft,
  type DayDraft,
  type WeekdayKey,
} from "./calendar-draft";
import { timeZoneOptions } from "./time-zones";
import "./calendar.css";

// Impostazioni → Calendario, fuso e lingua (piano M1b Task 10, mock-up `regole.html?sezione=calendario`): il fuso,
// i giorni e gli orari di lavoro, le chiusure con il loro motivo e la lingua dell'azienda, in una bozza sola (D9).
// Salvare passa da «Cosa cambia per il team» e manda una richiesta sola, con il calendario intero. La vede ogni
// persona attiva; la cambia chi ha `settings.calendar`, e mai il Super admin di Gioia365 (D13, questione 43: sono
// regole dell'azienda, anche se il server lo permetterebbe). La lingua dell'azienda non cambia la lingua
// dell'interfaccia di nessuno, nemmeno di chi salva (decisione 39, 30 settembre 2026): l'interfaccia segue la
// scelta della persona (`LocaleProvider`) e non legge mai `settings.defaultLocale`. In «Chiusure» il pulsante «Aggiungi
// le festività del …» mette nella bozza le festività nazionali che mancano, un anno alla volta (richiesta del product
// owner del 1° ottobre 2026): si salvano come ogni altra modifica, niente parte da solo.

/** Un avviso con il testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
type Feedback = { text: (t: Translate) => string };
type SearchedLocation = { pathname: string; search: unknown };

// Gli intervalli di «Dalle» e «Alle» dei mock-up, a passi di mezz'ora.
const FROM_RANGE = ["07:00", "12:00"] as const;
const TO_RANGE = ["13:00", "22:00"] as const;
// Lo stesso limite del server (`workingCalendarSchema`).
const MAX_REASON = 40;
// Perché una chiusura non entra: manca qualcosa, il giorno è già chiuso, il calendario è pieno.
const CLOSURE_ERROR = { missing: "calendar.closures.missing", duplicate: "calendar.closures.duplicate", full: "calendar.closures.full" } as const;

export function CalendarPage() {
  return (
    <SettingsLayout section="calendar">
      <CalendarSection />
    </SettingsLayout>
  );
}

function CalendarSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const settings = useQuery(settingsQuery(tab));
  const organization = access.context.organization.name;
  // Il contesto di piattaforma ha la funzione per il server, ma non cambia le regole di un'azienda (D13).
  const editable = access.can("settings.calendar") && !access.isPlatform;

  let body: ReactNode;
  if (settings.data) {
    body = <CalendarEditor tab={tab} settings={settings.data} editable={editable} />;
  } else if (settings.isError) {
    body = (
      <div className="g-calendar__problem">
        <Reason tone="warning" role="alert">{messageForError(t, settings.error)}</Reason>
        <Button onClick={() => void settings.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-calendar__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      {settings.data ? (
        <p className="g-calendar__muted">{t("calendar.lastSaved", { when: formatNumericDateTime(settings.data.timeZone, settings.data.updatedAt) })}</p>
      ) : null}
      <AboutBox title={t("calendar.about.title")}>
        <p>{t("calendar.about.text", { organization })}</p>
        <h2>{t("calendar.about.whyTitle")}</h2>
        <p>{t("calendar.about.why")}</p>
      </AboutBox>
      {editable ? null : <Reason>{access.isPlatform ? t("calendar.readOnlyPlatform", { organization }) : t("calendar.readOnly", { organization })}</Reason>}
      {body}
    </>
  );
}

function fieldsOf(details: Record<string, unknown> | undefined): string[] {
  const value = details?.fields;
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/** Un rifiuto del server, spiegato: i due controlli che l'interfaccia fa già a monte parlano come la barra. */
function saveError(t: Translate, error: unknown): string {
  if (error instanceof ApiRequestError && error.code === "validation_failed") {
    const fields = fieldsOf(error.details);
    if (fields.includes("workingCalendar.weeklyHours")) return t("calendar.problems.noOpenDay");
    if (fields.includes("workingCalendar.closureReasons")) return t("calendar.errors.closureReasons");
  }
  return messageForError(t, error);
}

type EditorProps = { tab: TabContext; settings: OrganizationSettingsView; editable: boolean };

function CalendarEditor({ tab, settings, editable }: EditorProps) {
  const t = useT();
  const toast = useToast();
  const updateSettings = useUpdateSettings(tab);
  const baseId = useId();
  const saved = useMemo(() => draftFromSettings(settings), [settings]);
  // La bozza nasce alla prima modifica; senza, la schermata mostra il salvato.
  const [edited, setEdited] = useState<CalendarDraft | null>(null);
  // La finestra «Cosa cambia per il team», con il rifiuto del server se il salvataggio si è fermato.
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [closureDay, setClosureDay] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [closureError, setClosureError] = useState<"missing" | "duplicate" | "full" | null>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);
  const dirtyNow = useRef(false);

  // Senza il permesso (che può sparire con un contesto riletto) si vede il salvato, non una bozza che non si può salvare.
  const draft = (editable ? edited : null) ?? saved;
  const changes = describeCalendarChanges(saved, draft, t);
  const dirty = editable && changes.length > 0;
  const problemCode = calendarProblems(draft);
  let problem: string | undefined;
  if (problemCode === "no_open_day") problem = t("calendar.problems.noOpenDay");
  else if (problemCode === "end_before_start") problem = t("calendar.problems.endBeforeStart");

  useEffect(() => {
    dirtyNow.current = dirty;
  });

  // Uscire con una bozza (un'altra sezione, un'altra pagina, un'altra azienda, «Esci») chiede prima (D9); lo stesso
  // indirizzo non è un'uscita. `beforeunload` vale per il ricaricamento e la chiusura della scheda del browser.
  const shouldBlockFn = useCallback(
    ({ current, next }: { current: SearchedLocation; next: SearchedLocation }) =>
      dirtyNow.current && !(current.pathname === next.pathname && JSON.stringify(current.search) === JSON.stringify(next.search)),
    [],
  );
  const enableBeforeUnload = useCallback(() => dirtyNow.current, []);
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true });

  function change(update: (next: CalendarDraft) => CalendarDraft) {
    if (!editable || saving) return;
    setEdited((current) => update(current ?? saved));
  }

  function setDay(key: WeekdayKey, update: Partial<DayDraft>) {
    change((next) => ({ ...next, weeklyHours: { ...next.weeklyHours, [key]: { ...next.weeklyHours[key], ...update } } }));
  }

  function addClosure() {
    if (!editable || saving) return;
    const reason = closureReason.trim();
    if (!closureDay || !reason) {
      setClosureError("missing");
      (closureDay ? reasonRef : dayRef).current?.focus();
      return;
    }
    if (draft.closures.some((closure) => closure.date === closureDay)) {
      setClosureError("duplicate");
      dayRef.current?.focus();
      return;
    }
    if (draft.closures.length >= MAX_CLOSURES) {
      setClosureError("full");
      return;
    }
    change((next) => ({ ...next, closures: [...next.closures, { date: closureDay, reason }].sort((a, b) => a.date.localeCompare(b.date)) }));
    setClosureDay("");
    setClosureReason("");
    setClosureError(null);
    dayRef.current?.focus();
  }

  /** Le festività nazionali che mancano, dell'anno che il pulsante dice, entrano nella bozza con il loro nome. */
  function addHolidays() {
    if (!editable || saving) return;
    const result = addNationalHolidays(draft, todayIn(draft.timeZone));
    if (result === null) return;
    if (result === "full") {
      setClosureError("full");
      return;
    }
    change(() => result.draft);
    setClosureError(null);
    toast.show(result.added === 1 ? t("calendar.closures.holidaysAddedOne", { year: result.year }) : t("calendar.closures.holidaysAdded", { count: result.added, year: result.year }));
  }

  function removeClosure(date: string) {
    change((next) => ({ ...next, closures: next.closures.filter((closure) => closure.date !== date) }));
    // Il cestino sparisce con la riga: il focus va alla riga per aggiungere, come nel mock-up.
    dayRef.current?.focus();
  }

  function discard() {
    setEdited(null);
    setClosureError(null);
    toast.show(t("calendar.saveBar.discarded"));
  }

  async function save() {
    if (!editable || savingNow.current || problemCode !== null) return;
    const body = updateBodyFrom(saved, draft);
    if (Object.keys(body).length === 0) {
      setConfirming(null);
      setEdited(null);
      return;
    }
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      await updateSettings.mutateAsync(body);
      // La conferma si chiude prima dell'avviso (D16).
      setConfirming(null);
      setEdited(null);
      toast.show(t("calendar.saveBar.saved"), "success");
    } catch (error) {
      // La finestra resta con la bozza e dice perché: si torna alle modifiche o si riprova.
      setConfirming({ error: { text: (tr) => saveError(tr, error) } });
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setEdited(null);
    setClosureError(null);
    if (blocker.status === "blocked") blocker.proceed();
  }

  const holidays = editable ? nextNationalHolidays(draft.closures, todayIn(draft.timeZone)) : null;
  const summary = changes.length === 1 ? t("calendar.saveBar.summaryOne") : t("calendar.saveBar.summary", { count: changes.length });
  const disabled = !editable || saving;

  return (
    <>
      <section className="g-calendar__section" aria-labelledby={`${baseId}-zone`}>
        <h2 id={`${baseId}-zone`}>{t("calendar.zone.title")}</h2>
        <Select
          id={`${baseId}-zone-select`}
          label={t("calendar.zone.label")}
          className="g-calendar__choice"
          value={draft.timeZone}
          onChange={(timeZone) => change((next) => ({ ...next, timeZone }))}
          options={timeZoneOptions(t, saved.timeZone)}
          disabled={disabled}
        />
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-hours`}>
        <h2 id={`${baseId}-hours`}>{t("calendar.hours.title")}</h2>
        <DataTable caption={t("calendar.hours.title")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("calendar.hours.day")}</th>
              <th scope="col">{t("calendar.hours.works")}</th>
              <th scope="col">{t("calendar.hours.from")}</th>
              <th scope="col">{t("calendar.hours.to")}</th>
            </tr>
          </thead>
          <tbody>
            {WEEKDAY_KEYS.map((key) => {
              const day = draft.weeklyHours[key];
              const name = t(WEEKDAY_NAME[key]);
              // Un giorno chiuso ha gli orari spenti, ma li conserva per quando lo si riapre.
              const timesOff = disabled || !day.open;
              return (
                <tr key={key}>
                  <th scope="row">{name}</th>
                  <td>
                    <Checkbox
                      id={`${baseId}-open-${key}`}
                      label={t("calendar.hours.works")}
                      aria-label={t("calendar.hours.worksDay", { day: name })}
                      checked={day.open}
                      disabled={disabled}
                      onChange={(open) => setDay(key, { open })}
                    />
                  </td>
                  <td>
                    <Select
                      id={`${baseId}-from-${key}`}
                      label={t("calendar.hours.fromDay", { day: name })}
                      hideLabel
                      className="g-calendar__time"
                      value={day.start}
                      onChange={(start) => setDay(key, { start })}
                      options={clockOptions(FROM_RANGE[0], FROM_RANGE[1], day.start).map((clock) => ({ value: clock, label: clock }))}
                      disabled={timesOff}
                    />
                  </td>
                  <td>
                    <Select
                      id={`${baseId}-to-${key}`}
                      label={t("calendar.hours.toDay", { day: name })}
                      hideLabel
                      className="g-calendar__time"
                      value={day.end}
                      onChange={(end) => setDay(key, { end })}
                      options={clockOptions(TO_RANGE[0], TO_RANGE[1], day.end).map((clock) => ({ value: clock, label: clock }))}
                      disabled={timesOff}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-closures`}>
        <h2 id={`${baseId}-closures`}>{t("calendar.closures.title")}</h2>
        <DataTable caption={t("calendar.closures.caption")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("calendar.closures.day")}</th>
              <th scope="col">{t("calendar.closures.reason")}</th>
              <th scope="col">
                <span className="g-visually-hidden">{t("calendar.closures.remove")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {draft.closures.length === 0 ? (
              <tr>
                <td colSpan={3} className="g-calendar__muted">
                  {t("calendar.closures.none")}
                </td>
              </tr>
            ) : (
              draft.closures.map((closure) => {
                const date = formatNumericDate(draft.timeZone, closure.date);
                return (
                  <tr key={closure.date}>
                    <th scope="row">{date}</th>
                    <td>
                      {closure.reason ? (
                        closure.reason
                      ) : (
                        <>
                          <span aria-hidden="true">—</span>
                          <span className="g-visually-hidden">{t("calendar.closures.noReason")}</span>
                        </>
                      )}
                    </td>
                    <td>
                      {editable ? (
                        <Button
                          variant="ghost"
                          className="g-calendar__remove"
                          aria-label={t("calendar.closures.removeNamed", { date })}
                          disabled={saving}
                          onClick={() => removeClosure(closure.date)}
                        >
                          <Icon name="trash" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </DataTable>
        {editable ? (
          <>
            <div className="g-calendar__add">
              <div className="g-calendar__add-day">
                <TextField
                  ref={dayRef}
                  id={`${baseId}-closure-day`}
                  type="date"
                  label={t("calendar.closures.dayLabel")}
                  value={closureDay}
                  min={todayIn(draft.timeZone)}
                  disabled={saving}
                  onChange={(event) => {
                    setClosureDay(event.target.value);
                    setClosureError(null);
                  }}
                />
              </div>
              <div className="g-calendar__add-reason">
                <TextField
                  ref={reasonRef}
                  id={`${baseId}-closure-reason`}
                  label={t("calendar.closures.reason")}
                  value={closureReason}
                  maxLength={MAX_REASON}
                  placeholder={t("calendar.closures.reasonPlaceholder")}
                  autoComplete="off"
                  disabled={saving}
                  onChange={(event) => {
                    setClosureReason(event.target.value);
                    setClosureError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addClosure();
                  }}
                />
              </div>
              <Button onClick={addClosure} disabled={saving}>
                <Icon name="plus" />
                {t("calendar.closures.add")}
              </Button>
            </div>
            {holidays ? (
              <div className="g-calendar__holidays">
                <Button onClick={addHolidays} disabled={saving}>
                  <Icon name="calendar" />
                  {t("calendar.closures.addHolidays", { year: holidays.year })}
                </Button>
                <p className="g-calendar__muted">{t("calendar.closures.holidaysHint")}</p>
              </div>
            ) : null}
            {closureError ? (
              <Reason tone="warning" role="alert">
                {t(CLOSURE_ERROR[closureError])}
              </Reason>
            ) : null}
          </>
        ) : null}
        <p className="g-calendar__muted">{t("calendar.closures.note")}</p>
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-language`}>
        <h2 id={`${baseId}-language`}>{t("calendar.language.title")}</h2>
        <Select
          id={`${baseId}-language-select`}
          label={t("calendar.language.label")}
          hint={t("calendar.language.hint")}
          className="g-calendar__choice"
          value={draft.defaultLocale}
          onChange={(value) => {
            const defaultLocale = locales.find((candidate) => candidate === value);
            if (defaultLocale) change((next) => ({ ...next, defaultLocale }));
          }}
          // Ogni lingua con il suo nome, uguale in tutte e due le lingue dell'interfaccia.
          options={[
            { value: "it", label: t("account.language.italian") },
            { value: "en", label: t("account.language.english") },
          ]}
          disabled={disabled}
        />
      </section>

      {dirty ? <SaveBar summary={summary} problem={problem} onDiscard={discard} onSave={() => setConfirming({ error: null })} saveLabel={t("calendar.saveBar.save")} saving={saving} /> : null}

      {confirming ? (
        <Dialog
          open
          title={t("calendar.confirm.title")}
          // Mentre la richiesta parte non si torna indietro: la finestra si chiude con l'esito.
          onClose={() => {
            if (!saving) setConfirming(null);
          }}
          footer={
            <>
              <Button onClick={() => setConfirming(null)} disabled={saving}>
                {t("calendar.confirm.back")}
              </Button>
              <Button variant="primary" onClick={() => void save()} disabled={saving} aria-busy={saving ? "true" : undefined}>
                {t("calendar.confirm.apply")}
              </Button>
            </>
          }
        >
          <ul>
            {changes.map((sentence) => (
              <li key={sentence}>{sentence}</li>
            ))}
          </ul>
          <p>{t("calendar.confirm.note")}</p>
          {confirming.error ? (
            <Reason tone="warning" role="alert">
              {confirming.error.text(t)}
            </Reason>
          ) : null}
        </Dialog>
      ) : null}

      {blocker.status === "blocked" ? (
        <Dialog
          open
          title={t("calendar.leave.title")}
          onClose={stay}
          footer={
            <>
              <Button onClick={leave}>{t("calendar.leave.discard")}</Button>
              <Button variant="primary" onClick={stay}>
                {t("calendar.leave.stay")}
              </Button>
            </>
          }
        >
          <p>{t("calendar.leave.text")}</p>
        </Dialog>
      ) : null}
    </>
  );
}
